import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CssBaseline,
  Divider,
  IconButton,
  Input,
  Link,
  Sheet,
  Snackbar,
  Stack,
  Switch,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/joy';
import Grid from '@mui/joy/Grid';
import HomeIcon from '@mui/icons-material/Home';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LaunchIcon from '@mui/icons-material/Launch';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { theme } from '../../main/theme/constants';
import LocationOptionList from '../../main/widgets/LocationOptionList';
import StatusOptionList from '../../main/widgets/StatusOptionList';
import TrackingEventTypeOptionList from '../../main/widgets/TrackingEventTypeOptionList';
import { usePermissions } from '../../main/hooks/usePermissions';
import { PERMISSIONS } from '../../main/context/AuthContext';
import PermissionGate from '../../main/components/PermissionGate';
import useTrackingEventTypeOptions from '../../main/hooks/useTrackingEventTypeOptions';
import { labelForEventTypeCode } from '../../main/utils/trackingEventLabels';
import {
  scanLookupShipmentPiece,
  scanResolve,
  scanUpdateShipmentPiece,
  bulkScanUpdateShipmentPieces,
  newClientEventId,
} from '../../main/API/shipmentPiece';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';
import {
  shipmentStatusChipSx,
  shipmentStatusJoyColor,
  shipmentStatusLabel,
} from '../../main/utils/shipmentStatusDisplay';

function playBeep(frequencyHz, durationMs, startTime = 0) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return startTime;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = frequencyHz;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.15, startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + durationMs / 1000);
    osc.start(startTime);
    osc.stop(startTime + durationMs / 1000);
    return startTime + durationMs / 1000;
  } catch (_) {
    return startTime;
  }
}
function playSuccessSound() {
  const t1 = playBeep(523, 80, 0);
  playBeep(659, 80, t1 + 0.05);
}
function playReplaySound() {
  playBeep(440, 70, 0);
}
function playFailedSound() {
  const t1 = playBeep(220, 120, 0);
  playBeep(160, 180, t1 + 0.08);
}

// Status colour / chip styling lives in `shipmentStatusDisplay` so the same
// rendering is reused everywhere (panel, manager, customer tracking, etc).
const statusColor = shipmentStatusJoyColor;
const statusLabel = shipmentStatusLabel;

// The five canonical statuses that trigger an automatic customer WhatsApp
// when their per-status `WHATSAPP_NOTIFY_ON_<STATUS>` setting is on. Mirrors
// FRONTEND_AUTOMATIC_WHATSAPP_NOTIFICATIONS_GUIDE §3.
const AUTO_NOTIFY_STATUSES = new Set([
  'shipping soon',
  'shipped',
  'arrived',
  'out for delivery',
  'delivered',
]);
const normalizeStatus = (s) => String(s || '').trim().toLowerCase();

export default function ScanPieceScreen() {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const canScanUpdate = hasPermission(PERMISSIONS.SHIPMENT_PIECES_SCAN_UPDATE);
  const canBulkScan = hasPermission(PERMISSIONS.SHIPMENT_PIECES_BULK_SCAN_UPDATE);
  const canLookup = hasPermission(PERMISSIONS.SHIPMENT_PIECES_SCAN_LOOKUP);
  const canSendWa = hasPermission(PERMISSIONS.WHATSAPP_SEND_SHIPMENT_NOTIFICATION);
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);
  const canUniversalResolve = hasPermission(PERMISSIONS.SCAN_RESOLVE);

  const { options: eventTypeOptions } = useTrackingEventTypeOptions(canListEventTypes);

  const [location, setLocation] = useState(null);
  const [status, setStatus] = useState('');
  const [eventTypeOpt, setEventTypeOpt] = useState(null);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [lookupOnly, setLookupOnly] = useState(false);
  const [soundOn, setSoundOn] = useState(true);

  const [barcode, setBarcode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]); // newest first
  const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });
  const [rollup, setRollup] = useState({ shipment_status_changes: [], whatsapp_sent_count: 0 });

  const [pendingBarcodes, setPendingBarcodes] = useState([]);
  const inputRef = useRef(null);

  const showSnack = useCallback((message, color = 'success') => {
    setSnack({ open: true, message, color });
  }, []);

  const beepFor = useCallback(
    (kind) => {
      if (!soundOn) return;
      if (kind === 'success') playSuccessSound();
      else if (kind === 'replay') playReplaySound();
      else playFailedSound();
    },
    [soundOn]
  );

  const buildBody = useCallback(() => {
    const body = {};
    if (location && location.id) body.location = location.id;
    if (status) body.status = status;
    if (eventTypeOpt && eventTypeOpt.code) body.event_type = eventTypeOpt.code;
    if (sendWhatsapp && canSendWa) body.send_whatsapp = true;
    return body;
  }, [location, status, eventTypeOpt, sendWhatsapp, canSendWa]);

  const summary = useMemo(() => {
    let succeeded = 0;
    let failed = 0;
    let replayed = 0;
    rows.forEach((row) => {
      if (row.ok) succeeded += 1;
      else failed += 1;
      if (row.idempotent_replay) replayed += 1;
    });
    return { succeeded, failed, replayed };
  }, [rows]);

  const mapLookupError = useCallback(
    async (e, code) => {
      const status409 = e && e.response && e.response.status === 409;
      const status400 = e && e.response && e.response.status === 400;
      const data = e && e.response && e.response.data;
      let errorCode = 'error';
      let errorText = translateAxiosErrorMessage(e, t) || t('scanPiece.result.error');
      if (status409 && data && data.error === 'use_universal_resolve') {
        if (canUniversalResolve) {
          try {
            const resolved = await scanResolve(code);
            const kind = resolved && resolved.data && resolved.data.kind;
            if (kind === 'shipment') errorText = t('scanPiece.result.tryUniversal');
            else if (kind === 'unit') errorText = t('scanPiece.result.tryUniversal');
            errorCode = 'use_universal_resolve';
          } catch (_) {
            errorText = t('scanPiece.result.tryUniversal');
            errorCode = 'use_universal_resolve';
          }
        } else {
          errorText = t('scanPiece.result.tryUniversal');
          errorCode = 'use_universal_resolve';
        }
      } else if (status400 && data && data.error === 'bad_barcode_format') {
        errorText = t('scanPiece.result.badFormat');
        errorCode = 'bad_barcode_format';
      } else if (status400 && data && data.error === 'bad_check_digit') {
        errorText = t('scanPiece.result.badCheck');
        errorCode = 'bad_check_digit';
      } else if (e && e.response && e.response.status === 404) {
        errorText = t('scanPiece.result.notFound');
        errorCode = 'piece_not_found';
      }
      return { errorCode, errorText };
    },
    [t, canUniversalResolve]
  );

  const lookupPieceByBarcode = useCallback(
    async (rawBarcode) => {
      const code = String(rawBarcode || '').trim();
      if (!code) return { ok: false, skipped: true };
      try {
        const lookupRes = await scanLookupShipmentPiece(code);
        const piece = lookupRes && lookupRes.data;
        if (!piece || !piece.id) {
          return {
            ok: false,
            barcode: code,
            errorCode: 'piece_not_found',
            error: t('scanPiece.result.notFound'),
          };
        }
        return { ok: true, piece, barcode: piece.barcode || code };
      } catch (e) {
        const { errorCode, errorText } = await mapLookupError(e, code);
        return { ok: false, barcode: code, errorCode, error: errorText };
      }
    },
    [mapLookupError, t]
  );

  const appendFailedLookupRow = useCallback((result) => {
    setRows((prev) => [{
      id: `${result.barcode}-${Date.now()}`,
      barcode: result.barcode,
      ok: false,
      errorCode: result.errorCode,
      error: result.error,
    }, ...prev]);
  }, []);

  // ---------- single piece scan ----------
  const submitSinglePieceScan = useCallback(
    async (rawBarcode) => {
      const code = String(rawBarcode || '').trim();
      if (!code) return;
      const lookup = await lookupPieceByBarcode(code);
      if (lookup.skipped) return;
      if (!lookup.ok) {
        beepFor('error');
        appendFailedLookupRow(lookup);
        return;
      }
      const { piece } = lookup;
      if (lookupOnly) {
        beepFor('success');
        setRows((prev) => [{
          id: `${piece.id}-${Date.now()}`,
          barcode: lookup.barcode,
          human_label: piece.human_label,
          piece,
          ok: true,
          lookup: true,
          idempotent_replay: false,
        }, ...prev]);
        return;
      }
      if (!canScanUpdate) {
        showSnack(t('permissions.noPermission'), 'danger');
        return;
      }
      try {
        const updateRes = await scanUpdateShipmentPiece(piece.id, {
          ...buildBody(),
          client_event_id: newClientEventId(),
        });
        const updated = updateRes && updateRes.data;
        const isReplay = Boolean(updated && updated.idempotent_replay);
        beepFor(isReplay ? 'replay' : 'success');
        setRows((prev) => [{
          id: `${piece.id}-${Date.now()}`,
          barcode: updated.barcode || lookup.barcode,
          human_label: updated.human_label || piece.human_label,
          piece: updated,
          ok: true,
          idempotent_replay: isReplay,
          whatsapp_sent: Boolean(updated.whatsapp_sent),
        }, ...prev]);
      } catch (e) {
        beepFor('error');
        const errorText = translateAxiosErrorMessage(e, t) || t('scanPiece.result.error');
        setRows((prev) => [{
          id: `${code}-${Date.now()}`,
          barcode: code,
          ok: false,
          errorCode: 'error',
          error: errorText,
        }, ...prev]);
      }
    },
    [
      lookupOnly,
      canScanUpdate,
      beepFor,
      buildBody,
      showSnack,
      t,
      lookupPieceByBarcode,
      appendFailedLookupRow,
    ]
  );

  // ---------- bulk piece scan ----------
  const submitBulkPieceScan = useCallback(async (barcodes) => {
    if (!canBulkScan) {
      showSnack(t('permissions.noPermission'), 'danger');
      return;
    }
    const toSubmit = Array.isArray(barcodes) ? barcodes : [];
    if (toSubmit.length === 0) return;
    setSubmitting(true);
    try {
      const res = await bulkScanUpdateShipmentPieces({
        barcodes: toSubmit,
        ...buildBody(),
      });
      const data = res && res.data;
      const results = (data && Array.isArray(data.results)) ? data.results : [];
      const newRows = results.map((r) => {
        const piece = r.piece || {};
        return {
          id: `${r.barcode}-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
          barcode: r.barcode,
          human_label: piece.human_label,
          piece: r.ok ? piece : null,
          ok: Boolean(r.ok),
          idempotent_replay: Boolean(r.idempotent_replay),
          whatsapp_sent: Boolean(r.whatsapp_sent),
          errorCode: r.error,
          error: r.ok ? null : r.detail || r.error || t('scanPiece.result.error'),
        };
      });
      if (newRows.some((r) => r.ok)) beepFor('success');
      else beepFor('error');
      setRows((prev) => [...newRows.reverse(), ...prev]);
      if (data && data.rollup) {
        setRollup({
          shipment_status_changes: Array.isArray(data.rollup.shipment_status_changes)
            ? data.rollup.shipment_status_changes
            : [],
          whatsapp_sent_count: Number(data.rollup.whatsapp_sent_count) || 0,
        });
        (data.rollup.shipment_status_changes || []).forEach((change) => {
          showSnack(
            t('scanPiece.rollup.shipmentNowStatus', {
              id: change.shipment_id,
              status: statusLabel(change.to, t),
            }),
            'primary'
          );
        });
      }
    } catch (e) {
      beepFor('error');
      const msg = translateAxiosErrorMessage(e, t) || t('scanPiece.result.error');
      showSnack(msg, 'danger');
    } finally {
      setSubmitting(false);
    }
  }, [canBulkScan, buildBody, beepFor, showSnack, t]);

  const handleScanSubmit = useCallback(async () => {
    const code = barcode.trim();
    if (!code) return;
    setBarcode('');
    if (bulkMode && !lookupOnly) {
      if (!canBulkScan) {
        showSnack(t('permissions.noPermission'), 'danger');
        return;
      }
      const normalized = code.toLowerCase();
      if (pendingBarcodes.some((bc) => String(bc).toLowerCase() === normalized)) {
        showSnack(t('scanPiece.result.alreadyQueued'), 'warning');
        if (inputRef.current) inputRef.current.focus();
        return;
      }
      setSubmitting(true);
      try {
        const lookup = await lookupPieceByBarcode(code);
        if (lookup.skipped) return;
        if (!lookup.ok) {
          beepFor('error');
          appendFailedLookupRow(lookup);
          return;
        }
        beepFor('success');
        setPendingBarcodes((prev) => [...prev, lookup.barcode]);
        setRows((prev) => [{
          id: `pending-${lookup.piece.id}-${Date.now()}`,
          barcode: lookup.barcode,
          human_label: lookup.piece.human_label,
          piece: lookup.piece,
          ok: true,
          pending: true,
        }, ...prev]);
      } finally {
        setSubmitting(false);
        if (inputRef.current) inputRef.current.focus();
      }
      return;
    }
    setSubmitting(true);
    try {
      await submitSinglePieceScan(code);
    } finally {
      setSubmitting(false);
      // refocus input for the next scan
      if (inputRef.current) inputRef.current.focus();
    }
  }, [
    barcode,
    bulkMode,
    lookupOnly,
    canBulkScan,
    pendingBarcodes,
    lookupPieceByBarcode,
    appendFailedLookupRow,
    beepFor,
    showSnack,
    submitSinglePieceScan,
    t,
  ]);

  const handleSubmitPending = useCallback(async () => {
    if (pendingBarcodes.length === 0) return;
    const barcodes = pendingBarcodes.slice();
    setPendingBarcodes([]);
    setRows((prev) => prev.filter((r) => !r.pending));
    await submitBulkPieceScan(barcodes);
  }, [pendingBarcodes, submitBulkPieceScan]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
          <Breadcrumbs sx={{ mb: 1 }}>
            <Link onClick={() => navigate('/home')} sx={{ cursor: 'pointer' }} underline="hover">
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t('nav.home')}
            </Link>
            <Typography>{t('nav.scanPiece')}</Typography>
          </Breadcrumbs>

          <Box sx={{ mb: 3 }}>
            <Typography level="h3" sx={{ fontWeight: 700 }}>
              {t('scanPiece.pageTitle')}
            </Typography>
            <Typography level="body-sm" sx={{ color: '#6B7280' }}>
              {t('scanPiece.subtitle')}
            </Typography>
          </Box>

          <Sheet variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 2 }}>
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCodeScannerIcon sx={{ color: '#5f09c2' }} />
              <Typography level="title-md">{t('scanPiece.panelTarget')}</Typography>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', flex: 1 }}>
                {t('scanPiece.panelTargetHint')}
              </Typography>
              <Tooltip title={soundOn ? t('scanPiece.soundOn') : t('scanPiece.soundOff')}>
                <IconButton variant="plain" onClick={() => setSoundOn((v) => !v)}>
                  {soundOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={2}>
              <Grid xs={12} md={4}>
                <LocationOptionList
                  defaultValue={location}
                  onChange={(_, v) => setLocation(v ?? null)}
                  label={t('shipmentPieces.location')}
                />
              </Grid>
              <Grid xs={12} md={4}>
                <StatusOptionList
                  status={status}
                  setStatus={setStatus}
                  allowEmpty
                  label={t('shipmentPieces.status')}
                />
              </Grid>
              {canListEventTypes && (
                <Grid xs={12} md={4}>
                  <TrackingEventTypeOptionList
                    label={t('shipmentPieces.eventType')}
                    required={false}
                    defaultValue={eventTypeOpt}
                    onChange={(_, v) => setEventTypeOpt(v ?? null)}
                  />
                </Grid>
              )}
            </Grid>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={bulkMode}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setBulkMode(next);
                    if (!next) {
                      setPendingBarcodes([]);
                      setRows((prev) => prev.filter((r) => !r.pending));
                    }
                  }}
                />
                <Typography level="body-sm">{t('scanPiece.bulkToggle')}</Typography>
              </Box>
              {canLookup && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch checked={lookupOnly} onChange={(e) => setLookupOnly(e.target.checked)} />
                  <Typography level="body-sm">{t('scanPiece.lookupToggle')}</Typography>
                </Box>
              )}
              {canSendWa && (
                <Tooltip title={t('scanPiece.forceSendWhatsappTooltip')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch checked={sendWhatsapp} onChange={(e) => setSendWhatsapp(e.target.checked)} />
                    <Typography level="body-sm">{t('scanPiece.forceSendWhatsapp')}</Typography>
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Sheet>

          <Sheet variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 2 }}>
            <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
              {t('scanPiece.scanInputLabel')}
            </Typography>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleScanSubmit(); }} sx={{ display: 'flex', gap: 1 }}>
              <Input
                slotProps={{ input: { ref: inputRef, autoFocus: true } }}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder={t('scanPiece.scanInputPlaceholder')}
                disabled={submitting}
                sx={{ flex: 1, fontFamily: 'monospace' }}
              />
              <Button type="submit" disabled={submitting || !barcode.trim()}>
                {t('scanPiece.submit')}
              </Button>
              {bulkMode && (
                <Button
                  variant="soft"
                  color="primary"
                  disabled={submitting || pendingBarcodes.length === 0}
                  onClick={handleSubmitPending}
                >
                  {t('scanPiece.submit')} ({pendingBarcodes.length})
                </Button>
              )}
            </Box>
          </Sheet>

          {rollup.shipment_status_changes.length > 0 && (
            <Sheet
              variant="soft"
              color="primary"
              sx={{ p: 2, borderRadius: '12px', mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography level="title-md" sx={{ flex: 1 }}>
                  {t('scanPiece.rollup.title')}
                </Typography>
                {rollup.whatsapp_sent_count > 0 && (
                  <Chip color="primary" variant="solid" size="sm">
                    {t('scanPiece.rollup.overrideSent', { count: rollup.whatsapp_sent_count })}
                  </Chip>
                )}
              </Box>
              <Stack spacing={0.75} sx={{ mb: 1 }}>
                {rollup.shipment_status_changes.map((change, idx) => {
                  const toNotifiable = AUTO_NOTIFY_STATUSES.has(normalizeStatus(change.to));
                  return (
                    <Box
                      key={`${change.shipment_id}-${idx}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                    >
                      <Typography level="body-sm" sx={{ fontWeight: 600, minWidth: 90 }}>
                        #{change.shipment_id}
                      </Typography>
                      <Chip
                        size="sm"
                        color={statusColor(change.from)}
                        variant="outlined"
                        sx={shipmentStatusChipSx(change.from)}
                      >
                        {statusLabel(change.from, t)}
                      </Chip>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>→</Typography>
                      <Chip
                        size="sm"
                        color={statusColor(change.to)}
                        variant="soft"
                        sx={shipmentStatusChipSx(change.to)}
                      >
                        {statusLabel(change.to, t)}
                      </Chip>
                      {toNotifiable && (
                        <Tooltip title={t('scanPiece.rollup.autoNotifyTooltip')}>
                          <Chip size="sm" color="success" variant="soft">
                            {t('scanPiece.rollup.autoNotifyBadge')}
                          </Chip>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Stack>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {t('scanPiece.rollup.dedupeHint')}
              </Typography>
            </Sheet>
          )}

          <Sheet variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography level="title-md" sx={{ flex: 1 }}>
                {t('scanPiece.scanList')}
              </Typography>
              <Chip color="success" variant="soft">
                {t('scanPiece.summary.succeeded')}: {summary.succeeded}
              </Chip>
              <Chip color="danger" variant="soft">
                {t('scanPiece.summary.failed')}: {summary.failed}
              </Chip>
              <Chip color="warning" variant="soft">
                {t('scanPiece.summary.replayed')}: {summary.replayed}
              </Chip>
              {rollup.whatsapp_sent_count > 0 && (
                <Chip color="primary" variant="soft">
                  {t('scanPiece.rollup.overrideSent', { count: rollup.whatsapp_sent_count })}
                </Chip>
              )}
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => {
                  setRows([]);
                  setPendingBarcodes([]);
                  setRollup({ shipment_status_changes: [], whatsapp_sent_count: 0 });
                }}
                disabled={rows.length === 0}
                title={t('scanPiece.clearList')}
              >
                <DeleteSweepIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {rows.length === 0 ? (
              <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 3, textAlign: 'center' }}>
                {t('scanPiece.scanListEmpty')}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {rows.map((row) => {
                  const piece = row.piece || {};
                  return (
                    <Sheet
                      key={row.id}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: '10px',
                        borderColor: row.pending
                          ? 'warning.300'
                          : row.ok
                            ? row.idempotent_replay
                              ? 'warning.300'
                              : 'success.300'
                            : 'danger.300',
                        bgcolor: row.pending
                          ? 'warning.softBg'
                          : row.ok
                            ? row.idempotent_replay
                              ? 'warning.softBg'
                              : 'success.softBg'
                            : 'danger.softBg',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography level="title-md" sx={{ minWidth: 90 }}>
                          {row.human_label || piece.human_label || '—'}
                        </Typography>
                        <Typography
                          level="body-xs"
                          sx={{ fontFamily: 'monospace', color: 'text.tertiary', flex: 1 }}
                        >
                          {row.barcode}
                        </Typography>
                        {row.ok != null && (
                          <Chip
                            size="sm"
                            color={
                              row.pending
                                ? 'warning'
                                : row.ok
                                  ? (row.idempotent_replay ? 'warning' : 'success')
                                  : 'danger'
                            }
                            variant="soft"
                          >
                            {row.pending
                              ? t('scanPiece.result.queued')
                              : row.ok
                                ? (row.idempotent_replay
                                    ? t('scanPiece.result.alreadyScanned')
                                    : t('scanPiece.result.scanned'))
                                : (row.error || t('scanPiece.result.error'))}
                          </Chip>
                        )}
                        {piece && piece.status && (
                          <Chip
                            size="sm"
                            color={statusColor(piece.status)}
                            variant="soft"
                            sx={shipmentStatusChipSx(piece.status)}
                          >
                            {statusLabel(piece.status, t)}
                          </Chip>
                        )}
                        {piece && piece.event_type && (
                          <Chip size="sm" variant="outlined" color="neutral">
                            {labelForEventTypeCode(piece.event_type, eventTypeOptions)}
                          </Chip>
                        )}
                        {piece && piece.location && (
                          <Chip size="sm" variant="outlined" color="neutral">
                            {(piece.location && (piece.location.name || piece.location)) || '—'}
                          </Chip>
                        )}
                        {row.whatsapp_sent && (
                          <Tooltip title={t('scanPiece.rollup.overrideRowTooltip')}>
                            <Chip size="sm" color="primary" variant="soft">
                              {t('scanPiece.rollup.overrideRowBadge')}
                            </Chip>
                          </Tooltip>
                        )}
                        {row.ok && piece && piece.shipment && (
                          <Tooltip title={t('shipmentPieces.actions.openShipment')}>
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="neutral"
                              onClick={() =>
                                window.open(`/Shipment/${piece.shipment}/EditShipment`, '_blank', 'noopener')
                              }
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Sheet>
                  );
                })}
              </Stack>
            )}
          </Sheet>
        </Box>
      </Box>

      <Snackbar
        autoHideDuration={3500}
        open={snack.open}
        variant="soft"
        color={snack.color}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); }}
      >
        {snack.message}
      </Snackbar>
    </ThemeProvider>
  );
}

// Marker so RolePermissionsByPageEditor's auto-detection finds these as gated:
// eslint-disable-next-line no-unused-vars
const _gateMarker = (
  <PermissionGate permissions={[PERMISSIONS.SHIPMENT_PIECES_SCAN_UPDATE, PERMISSIONS.SHIPMENT_PIECES_SCAN_LOOKUP, PERMISSIONS.SHIPMENT_PIECES_BULK_SCAN_UPDATE]} />
);
